/**
 * SendAccessLinkModal.js
 * Modal to generate, copy, and send athlete portal access link
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Form, Alert, Spinner, InputGroup } from 'react-bootstrap';
import { athleteAccessService } from '../../services/api';
import { isMinor as checkIsMinor } from '../../utils/userHelpers';

const SendAccessLinkModal = ({ show, onHide, user, onSuccess, onError }) => {
  const [loading, setLoading] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);
  const [tokenData, setTokenData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const isMinor = useCallback(() => {
    return checkIsMinor(user?.dateOfBirth, user?.fiscalCode);
  }, [user?.dateOfBirth, user?.fiscalCode]);

  const generateToken = useCallback(async () => {
    if (!user?.id) return;
    try {
      setLoading(true);
      setError('');
      const result = await athleteAccessService.createToken(user.id);
      if (result.success) {
        setTokenData(result.data);
      } else {
        setError(result.message || 'Errore nella generazione del link');
      }
    } catch (err) {
      setError('Errore di connessione al server');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (show && user) {
      setError('');
      setSuccess('');
      setCopied(false);
      setTokenData(null);

      // Determine initial recipient email
      if (isMinor()) {
        if (user.parent?.email && user.parent.email.trim()) {
          setRecipientEmail(user.parent.email.trim());
        } else if (user.loginEmail && user.loginEmail.trim()) {
          setRecipientEmail(user.loginEmail.trim());
        } else if (user.email && user.email.trim()) {
          setRecipientEmail(user.email.trim());
        } else {
          setRecipientEmail('');
        }
      } else if (user.loginEmail && user.loginEmail.trim()) {
        setRecipientEmail(user.loginEmail.trim());
      } else if (user.email && user.email.trim()) {
        setRecipientEmail(user.email.trim());
      } else {
        setRecipientEmail('');
      }

      // Generate access link
      generateToken();
    }
  }, [show, user, isMinor, generateToken]);

  const getFullAccessUrl = () => {
    if (!tokenData?.token) return '';
    return `${window.location.origin}/portal/setup/${tokenData.token}`;
  };

  const handleCopyLink = () => {
    const url = getFullAccessUrl();
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(url);
    } else {
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    if (!recipientEmail.trim()) {
      setError('Inserisci un indirizzo email valido');
      return;
    }

    try {
      setSendLoading(true);
      setError('');
      setSuccess('');
      const result = await athleteAccessService.sendAccessLink(user.id, recipientEmail.trim());
      if (result.success) {
        setSuccess(result.message || `Link inviato con successo a ${recipientEmail}`);
        if (onSuccess) {
          onSuccess(result.message || `Link inviato a ${recipientEmail}`);
        }
      } else {
        setError(result.message || 'Errore durante l\'invio dell\'email');
        if (onError) {
          onError(result.message);
        }
      }
    } catch (err) {
      setError('Errore di connessione al server');
      console.error(err);
    } finally {
      setSendLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fas fa-key me-2 text-primary"></i>
          Link di Accesso Portale - {user?.firstName} {user?.lastName}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
        {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2 text-muted">Generazione del link di accesso in corso...</p>
          </div>
        ) : (
          <>
            {(() => {
              const validityDays = tokenData?.validityDays || 60;
              const validityLabel = validityDays === 1 ? '1 giorno' : `${validityDays} giorni`;
              return (
                <>
                  <p className="text-muted mb-3">
                    Questo link permette all'atleta (o genitore per i minorenni) di effettuare il primo accesso, confermare l'email di login e creare la propria password. Il link ha una validità di <strong>{validityLabel}</strong>.
                  </p>

                  {tokenData && (
                    <div className="mb-4">
                      <Form.Label className="fw-bold">Link di accesso generato:</Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="text"
                          value={getFullAccessUrl()}
                          readOnly
                          className="bg-light font-monospace small"
                        />
                        <Button
                          variant={copied ? 'success' : 'outline-primary'}
                          onClick={handleCopyLink}
                        >
                          <i className={`fas ${copied ? 'fa-check' : 'fa-copy'} me-1`}></i>
                          {copied ? 'Copiato!' : 'Copia'}
                        </Button>
                      </InputGroup>
                      <small className="text-muted mt-1 d-block">
                        Scadenza: {new Date(tokenData.expiresAt).toLocaleString('it-IT')} ({validityLabel})
                      </small>
                    </div>
                  )}
                </>
              );
            })()}

            <hr />

            <div className="mt-3">
              <h6 className="fw-bold mb-3">
                <i className="fas fa-paper-plane me-2 text-primary"></i>
                Invia direttamente via Email
              </h6>
              <Form onSubmit={handleSendEmail}>
                <Form.Group className="mb-3">
                  <Form.Label>Destinatario Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={recipientEmail}
                    onChange={(e) => setRecipientEmail(e.target.value)}
                    placeholder="email@esempio.com"
                    required
                  />
                  <Form.Text className="text-muted">
                    {isMinor()
                      ? (user.parent?.email && user.parent.email.trim()
                          ? 'Atleta minorenne: pre-impostata l\'email del genitore/tutore.'
                          : (user.loginEmail || user.email
                              ? 'Atleta minorenne: genitore senza email, pre-impostata l\'email dell\'atleta.'
                              : 'Atleta minorenne: nessuna email disponibile.'))
                      : 'Pre-impostata l\'email di contatto dell\'atleta.'}
                  </Form.Text>
                </Form.Group>

                <div className="d-flex justify-content-end">
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={sendLoading || !tokenData}
                  >
                    {sendLoading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Invio in corso...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-paper-plane me-2"></i>
                        Invia Email
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Chiudi
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SendAccessLinkModal;
